<script>
import Breadcrumbs from '@/components/MarketplaceComponents/Breadcrumbs.vue'
import PageTitle from '@/components/MarketplaceComponents/PageTitle.vue'
import BaseSelect from '@/components/BaseSelect/BaseSelect.vue'
import Tabs from '@/components/Tabs/Tabs.vue'
import MultiSelect from '@/components/MultiSelect/MultiSelect.vue'
import NumberInput from '@/components/NumberInput/NumberInput.vue'
import FormInput from '@/components/FormInput/FormInput.vue'
import CheckboxInput from '@/components/CheckboxInput/CheckboxInput.vue'
import StoreService from '@/services/StoreService'
import { mapActions, mapState } from 'vuex'

export default {
  components: {
    Tabs,
    CheckboxInput,
    Breadcrumbs,
    PageTitle,
    BaseSelect,
    MultiSelect,
    NumberInput,
    FormInput
  },

  data () {
    return {
      activeServiceType: this.$route.query.type || 'game',
      app: this.$route.query.app || '',
      category: this.$route.query.category || '',
      price: '0',
      amount: '1',
      description: {},
      shortDescription: {},
      additionalData: {},
      creationInProgress: false,
      autoDataCheckbox: true,
      autoData: '',
      variantsAutoData: [],
      notLimited: false,
      newOptionModal: false,
      options: undefined,
      newOptionName: '',
      newOptionType: '',
      newOptionComment: '',
      newOptionId: undefined,
      newOptionOptions: [],
      optionTypes: [
        {
          value: 'text',
          title: 'Text Area'
        },
        {
          value: 'variants',
          title: 'Options'
        }
      ],
      activeLangIndex: 0,
      langs: [
        {
          value: 'en',
          title: 'EN'
        },
        {
          value: 'ru',
          title: 'RU'
        },
        {
          value: 'kk',
          title: 'KK'
        },
        {
          value: 'de',
          title: 'DE'
        },
        {
          value: 'es',
          title: 'ES'
        },
        {
          value: 'pt',
          title: 'PT'
        },
        {
          value: 'it',
          title: 'IT'
        }
      ]
    }
  },

  computed: {
    ...mapState('user', ['user']),
    ...mapState('marketplace', ['apps', 'currencies']),

    breadcrumbs () {
      return [
        { title: this.$t('store'), to: '/' },
        {
          title: this.$t('addNewService')
        }
      ]
    },

    activeLang () {
      return this.langs[this.activeLangIndex]
    },

    showRub () {
      return this.$i18n.locale === 'ru'
    },

    rubCurrencyRate () {
      return this.currencies?.find(c => c.code === 'RUB')?.exchange_rate || 0
    },

    priceInRub () {
      if (this.price === '') {
        return '...'
      }

      return Math.ceil(+this.price / (this.rubCurrencyRate * 0.98))
    },

    minVariantPriceInRub () {
      if (this.minVariantPrice === '') {
        return '...'
      }

      return Math.ceil(+this.minVariantPrice / (this.rubCurrencyRate * 0.98))
    },

    goBackPath () {
      return '/'
    },

    filteredApps () {
      if (!this.apps) {
        return undefined
      }

      return this.apps.filter(app => app.app_type === this.activeServiceType).map(app => ({ ...app, value: `${app.id}` }))
    },

    currentApp () {
      if (!this.app || this.app === '') {
        return undefined
      }

      return this.filteredApps?.find(app => app.id === +this.app)
    },

    currentAppName () {
      return this.currentApp?.title || '-'
    },

    appCategories () {
      return this.currentApp?.categories?.map(category => ({
        title: category?.category_name,
        value: category?.id?.toFixed(0)
      }))
    },

    categoryName () {
      if (!this.category || this.category === '') {
        return '-'
      }

      return this.appCategories?.find(c => c.value === this.category)?.title || '-'
    },

    categoryId () {
      if (!this.category || this.category === '') {
        return 0
      }

      return +(this.appCategories?.find(c => c.value === this.category)?.value || 0)
    },

    additionalFields () {
      if (!this.category || this.category === '') {
        return []
      }

      return this.currentApp?.categories?.find(c => c.id?.toFixed(0) === this.category)?.fields || []
    },

    customerPrice () {
      if (this.minVariantPrice) {
        return (Math.ceil((+this.minVariantPrice * 1.05) * 100) / 100).toFixed(2)
      }

      if (this.price === 0) {
        return '-'
      }

      return (Math.ceil((this.price * 1.05) * 100) / 100).toFixed(2)
    },

    minVariantPrice () {
      const [main] = [...(this.productVariants || [])].sort((a, b) => +a.price - +b.price)

      return main?.price
    },

    canCreate () {
      return this.currentApp &&
        this.categoryName !== '-' &&
        (this.price > 0 || +this.minVariantPrice > 0) &&
        this.description.en &&
        this.description.en.trim() !== '' &&
        this.shortDescription?.en &&
        this.shortDescription?.en.trim() !== ''
    },

    goodsCounter () {
      if (this.notLimited) {
        return '1'
      }
      return this.autoData?.split(/(?:\r\n|\r|\n)/g)?.length
    },

    productVariants () {
      return this.options?.find(o => o.type === 'variants')?.options
    },

    variantsStocksSum () {
      if (this.notLimited) {
        return '∞'
      } else if (this.autoDataCheckbox) {
        return this.productVariants?.map(v => this.optionCountAmountWithAutoDelivery(v)).reduce((a, b) => +a + +b, 0)
      }
      return this.productVariants?.map(v => v.price || 1).reduce((a, b) => +a + +b, 0)
    }
  },

  watch: {
    activeServiceType () {
      this.clear()
    }
  },

  mounted () {
    if (!this.apps) {
      this.fetchApps()
    }
  },

  methods: {
    ...mapActions('modal', ['showModal']),
    ...mapActions('marketplace', ['fetchApps']),

    clear () {
      this.app = ''
      this.category = ''
      this.price = '0'
      this.amount = '1'
      this.description = {}
      this.shortDescription = {}
      this.additionalData = {}
    },

    inRub (price) {
      if (price === '') {
        return '...'
      }

      return Math.ceil(+price / (this.rubCurrencyRate * 0.98))
    },

    selectorVariantStyles (type) {
      return {
        [this.$style.selector__variant]: true,
        [this.$style.selector__variantActive]: type === this.activeServiceType
      }
    },

    setActiveLang ({ index }) {
      console.log('index', index)
      this.activeLangIndex = index
    },

    roundStyles (type) {
      return {
        [this.$style.selector__iconRound]: true,
        [this.$style.selector__iconRoundActive]: type === this.activeServiceType
      }
    },

    setActiveServiceType (value) {
      if (this.canCreate) {
        this.showModal({
          component: 'ModalMessage',
          data: {
            title: this.$t('payAttention'),
            text: this.$t('newProductDataWillBeDeleted'),
            type: 'info',
            buttons: [
              {
                props: { isPrimary: true },
                title: this.$t('buttonAccept'),
                shouldClose: true,
                action: () => {
                  this.activeServiceType = value
                }
              },
              {
                props: { isSecondary: true },
                title: this.$t('buttonClose'),
                shouldClose: true
              }
            ]
          }
        })
      } else {
        this.activeServiceType = value
      }
    },

    setApp (event) {
      this.app = event
      if (this.appCategories?.length === 1) {
        this.category = this.appCategories[0].value
      } else {
        this.category = ''
      }
      this.additionalData = {}
    },

    setAppCategory (event) {
      this.category = event
      this.additionalData = {}
    },

    enumToOptions (enumData) {
      return enumData.map(value => ({
        title: value,
        value
      }))
    },

    addOption () {
      this.newOptionModal = false

      const option = {
        id: this.newOptionId || new Date(),
        title: this.newOptionName,
        type: this.newOptionType,
        comment: this.newOptionComment
      }

      if (this.newOptionType === 'variants') {
        option.options = this.newOptionOptions
      }

      const edit = this.options?.find(o => o.id === option.id)

      if (edit) {
        edit.title = option.title
        edit.type = option.type
        edit.comment = option.comment
        edit.options = option.options
      } else if (!this.options) {
        this.options = [
          option
        ]
      } else {
        this.options.push(option)
      }

      this.newOptionType = ''
      this.newOptionName = ''
      this.newOptionComment = ''
      this.newOptionId = undefined
      this.newOptionOptions = []
    },

    editOption (option) {
      this.newOptionName = option.title
      this.newOptionComment = option.comment
      this.newOptionType = option.type
      this.newOptionId = option.id
      this.newOptionOptions = option.options

      this.openAddOption()
    },

    addOptionOption () {
      this.newOptionOptions.push({
        title: '',
        price: 0,
        isDefault: false,
        quantity: 1,
        id: new Date()
      })
    },

    deleteOption (option) {
      this.options = this.options.filter(o => o.id !== option.id)
    },

    deleteOptionOption (id) {
      this.newOptionOptions = this.newOptionOptions.filter(oo => oo.id !== id)
    },

    optionCountAmountWithAutoDelivery (variant) {
      return this.variantsAutoData[variant.id]?.split(/(?:\r\n|\r|\n)/g)?.length
    },

    async createManyProducts () {
      const [main, ...variants] = [...(this.productVariants || [])].sort((a, b) => +a.price - +b.price)
      console.log('main:', main, ', variants:', variants)

      const mainId = await this.createProduct(main, undefined)

      console.log('MAIN ID:', mainId)

      for (const variant of variants) {
        console.log('CREATE VARIANT', variant)
        await this.createProduct(variant, mainId)
      }

      console.log('FINISHED')

      this.showModal({
        component: 'ModalMessage',
        data: {
          title: this.$t('success'),
          type: 'info',
          text: this.$t('success')
        }
      })
      this.clear()
      try {
        window?.ym?.(96913300, 'reachGoal', 'placedthegoods')
      } catch (e) {
        console.log(e)
      }
      this.$router.push(this.localeLocation('/profile/goods'))
    },

    async createProduct (variant, mainId) {
      if (!this.canCreate || (this.creationInProgress && !mainId)) {
        return
      }

      this.creationInProgress = true
      this.$nuxt.$loading.start()

      // eslint-disable-next-line camelcase
      const seo_data = {}

      for (const key of Object.keys(this.shortDescription)) {
        seo_data[key] = {
          h1: this.shortDescription[key],
          description: this.description[key] || ''
        }
      }

      const product = {
        title: `${variant?.title ? '[' + variant.title + '] ' : ''}${this.currentAppName} ${this.categoryName}`,
        description: this.description?.en,
        short_description: this.shortDescription?.en,
        brand: this.currentAppName,
        category: this.categoryName,
        images: [this.currentApp.cover],
        gtin: '',
        specifications: this.additionalData ? Object.keys(this.additionalData).map(key => ({
          key,
          value: this.additionalData[key]
        })) : [],
        min_price: Math.round(this.price * 100),
        max_price: (+this.customerPrice * 100).toFixed(0),
        r_price: Math.round(this.price * 100),
        external_id: `${this.user?.id}_${new Date().getTime()}`,
        partner: this.user?.full_name,
        account_id: this.user?.id,
        quantity: this.notLimited ? '1111111' : this.autoDataCheckbox ? this.goodsCounter : this.amount,
        application_category_id: this.categoryId,
        seo_data
      }

      if (variant) {
        product.min_price = Math.round(variant.price * 100)
        product.max_price = Math.ceil(variant.price * 105).toFixed(0)
        product.r_price = Math.round(variant.price * 100)

        product.quantity = this.notLimited
          ? '1111111'
          : this.autoDataCheckbox
            ? this.optionCountAmountWithAutoDelivery(variant)
            : variant.amount
      }

      if (mainId) {
        product.parent_id = mainId
      }

      product.fields = this.options

      if (this.autoDataCheckbox) {
        if (variant) {
          product.auto_messages = this.variantsAutoData[variant.id]
        } else {
          product.auto_messages = this.autoData
        }
      }

      try {
        const { data } = await StoreService.addMarketplaceProduct(this.$axios, product)
        this.$nuxt.$loading.finish()

        if (data?.data?.result) {
          return data.data.result
        }
      } catch (e) {
        this.$nuxt.$loading.finish()
        this.showModal({
          component: 'ModalMessage',
          data: {
            title: this.$t('error'),
            type: 'error',
            text: this.$t('error')
          }
        })
        this.$router.push(this.localeLocation('/'))
      }
    },

    openAddOption () {
      this.newOptionModal = true
    },

    closeNewOptionModal () {
      this.newOptionModal = false
      this.newOptionType = ''
      this.newOptionName = ''
      this.newOptionComment = ''
      this.newOptionId = undefined
      this.newOptionOptions = []
    }
  }
}
</script>

<template>
  <div :class="$style.page">
    <Breadcrumbs :list="breadcrumbs" />

    <div :class="$style.page__body">
      <PageTitle with-go-back :go-back-path="goBackPath" :title="$t('addNewService')" />

      <p :class="$style.subtitle">
        {{ $t("serviceType") }}
      </p>

      <div :class="$style.selector">
        <div :class="selectorVariantStyles('game')" @click="setActiveServiceType('game')">
          <div :class="$style.selector__icon">
            <div :class="roundStyles('game')">
              <div v-if="activeServiceType === 'game'" :class="$style.selector__iconDot"></div>
            </div>
          </div>
          {{ $t("games") }}
        </div>
        <div :class="selectorVariantStyles('app')" @click="setActiveServiceType('app')">
          <div :class="$style.selector__icon">
            <div :class="roundStyles('app')">
              <div v-if="activeServiceType === 'app'" :class="$style.selector__iconDot"></div>
            </div>
          </div>
          {{ $t("offer_type_app_downloads") }}
        </div>
        <div :class="selectorVariantStyles('social')" @click="setActiveServiceType('social')">
          <div :class="$style.selector__icon">
            <div :class="roundStyles('social')">
              <div v-if="activeServiceType === 'social'" :class="$style.selector__iconDot"></div>
            </div>
          </div>
          {{ $t("socialNetworks") }}
        </div>
        <div :class="selectorVariantStyles('offerwall')" @click="setActiveServiceType('offerwall')">
          <div :class="$style.selector__icon">
            <div :class="roundStyles('offerwall')">
              <div v-if="activeServiceType === 'offerwall'" :class="$style.selector__iconDot"></div>
            </div>
          </div>
          {{ $t("offerwalls") }}
        </div>
      </div>

      <p :class="$style.subtitle">
        {{ $t("generalInfoAboutService") }}
      </p>

      <template v-if="apps">
        <p :class="$style.label">
          {{ $t(activeServiceType) }}
        </p>

        <BaseSelect
          :value="app"
          :options="filteredApps"
          :placeholder="$t('choose')"
          @input="setApp($event)"
        />
      </template>

      <template v-if="currentApp">
        <p :class="$style.label">
          {{ $t('category') }}
        </p>

        <BaseSelect
          :value="category"
          :options="appCategories"
          :placeholder="$t('choose')"
          @input="setAppCategory($event)"
        />
      </template>

      <template v-if="category && category !== ''">
        <div v-for="additionalField in additionalFields" :key="additionalField.name || additionalField.title">
          <p :class="$style.label">
            {{ additionalField.name || additionalField.title }}
          </p>

          <NumberInput
            v-if="additionalField.type === 'number'"
            v-model="additionalData[`${additionalField.name || additionalField.title}`]"
            :class="$style.fullWidth"
            :increment="1"
            :min="0"
            :fixed="0"
            full-width
          />

          <MultiSelect
            v-else-if="additionalField.enum && additionalField.multi"
            v-model="additionalData[`${additionalField.name || additionalField.title}`]"
            :options="enumToOptions(additionalField.enum)"
            :placeholder="$t('choose')"
          />
          <BaseSelect
            v-else-if="additionalField.enum"
            v-model="additionalData[`${additionalField.name || additionalField.title}`]"
            :options="enumToOptions(additionalField.enum)"
            :placeholder="$t('choose')"
          />
          <FormInput v-else v-model="additionalData[`${additionalField.name || additionalField.title}`]" />
        </div>
      </template>

      <template v-if="apps">
        <div :class="$style.checkbox">
          <CheckboxInput v-model="autoDataCheckbox" />
          <span :class="$style.checkbox__label">
            {{ $t("autoDelivery") }}
          </span>
        </div>

        <div :class="$style.optionsPanel">
          <div :class="$style.block">
            <div :class="$style.block__header">
              <p :class="$style.block__title">
                {{ $t("options") }}
              </p>
            </div>

            <div :class="$style.block__body">
              <div v-if="!options" :class="$style.noOptions">
                <InlineSvg :src="require(`~/assets/img/icons/sad.svg`)" :class="$style.noOptions__icon" />
                <p :class="$style.noOptions__text">
                  {{ $t('noCreatedOptions') }}
                </p>
              </div>
              <div v-else :class="$style.optionsList">
                <div v-for="option in options" :key="option.id" :class="$style.option">
                  <div :class="$style.option__title">
                    <span>{{ option.title }}</span>
                    <div>
                      <InlineSvg :class="$style.option__icon" :src="require(`~/assets/img/icons/edit.svg`)" @click="editOption(option)" />
                      <InlineSvg :class="$style.option__icon" :src="require(`~/assets/img/icons/trash.svg`)" @click="deleteOption(option)" />
                    </div>
                  </div>

                  <p v-if="option.comment && option.comment !== ''" :class="$style.option__comment">
                    {{ option.comment }}
                  </p>

                  <template v-if="option.type === 'text'">
                    <div :class="$style.option__textExample" />
                  </template>

                  <template v-else-if="option.type === 'variants'">
                    <div v-for="variant in option.options" :key="variant.id" :class="$style.option__variantExample">
                      <div :class="$style.option__variantExampleLeft">
                        <CheckboxInput />
                        <span :class="$style.option__variantExampleName">{{ variant.title }}</span>
                        <span v-if="notLimited" :class="$style.option__variantExampleAmount">(∞)</span>
                        <span v-else-if="autoDataCheckbox" :class="$style.option__variantExampleAmount">({{ optionCountAmountWithAutoDelivery(variant) }})</span>
                        <span v-else :class="$style.option__variantExampleAmount">({{ variant.amount || 1 }})</span>
                      </div>

                      <Currency type="money" real size="16" is-reversed>
                        <span :class="$style.option__variantExamplePrice">{{ variant.price }}</span>
                      </Currency>
                    </div>
                  </template>
                </div>
              </div>

              <Button is-primary :class="$style.save" @click="openAddOption">
                {{ $t("addOption") }}
              </Button>
            </div>
          </div>

          <div v-if="newOptionModal" :class="$style.modal">
            <div :class="$style.modal__main">
              <div :class="$style.modal__title">
                {{ $t("addOption") }}
              </div>

              <p :class="$style.label">
                {{ $t('optionName') }}
              </p>

              <FormInput v-model="newOptionName" />

              <p :class="$style.label">
                {{ $t('optionType') }}
              </p>

              <BaseSelect
                v-model="newOptionType"
                :options="optionTypes"
                :placeholder="$t('choose')"
                :with-empty-value="false"
              />

              <p :class="$style.label">
                {{ $t('optionComment') }} <span :class="$style.label__comment">{{ $t('notRequired') }}</span>
              </p>

              <FormInput v-model="newOptionComment" type="textarea" />

              <template v-if="newOptionType === 'variants'">
                <div :class="$style.optionsList">
                  <div v-for="optionOption in newOptionOptions" :key="optionOption.id" :class="$style.optionOption">
                    <div :class="$style.optionOption__title">
                      <FormInput v-model="optionOption.title" :placeholder="$t('title')" />
                      <InlineSvg :class="$style.option__icon" :src="require(`~/assets/img/icons/trash.svg`)" @click="deleteOptionOption(optionOption.id)" />
                    </div>

                    <div :class="$style.twoColumn">
                      <div>
                        <div>
                          <p :class="$style.label">
                            {{ $t('price') }}
                          </p>

                          <NumberInput
                            v-model="optionOption.price"
                            :class="$style.fullWidth"
                            :increment="0.01"
                            :fixed="2"
                            real
                            full-width
                            currency="money"
                          />
                        </div>
                        <div v-if="showRub">
                          <p :class="$style.label">
                            Цена в рублях:
                          </p>
                          <Currency type="money" size="24" is-reversed rub>
                            <span :class="$style.subtitle">{{ inRub(optionOption.price) }}</span>
                          </Currency>
                        </div>
                      </div>
                      <div v-if="!notLimited && !autoDataCheckbox">
                        <div>
                          <p :class="$style.label">
                            {{ $t('amount') }}
                          </p>

                          <NumberInput
                            v-model="optionOption.amount"
                            :class="$style.fullWidth"
                            :increment="1"
                            :max="9000000"
                            :min="1"
                            :fixed="0"
                            full-width
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <Button is-secondary :class="$style.save" @click="addOptionOption">
                  {{ $t('addVariant') }}
                </Button>
              </template>

              <Button is-primary :class="$style.addBtn" :disabled="!newOptionName?.length || !newOptionType?.trim().length" @click="addOption">
                {{ $t('buttonSave') }}
              </Button>
              <Button is-secondary :class="$style.closeBtn" @click="closeNewOptionModal">
                {{ $t('buttonClose') }}
              </Button>
            </div>
          </div>
        </div>

        <template v-if="autoDataCheckbox && !productVariants">
          <p :class="$style.subtitle">
            {{ $t("goods") }}
            <span v-if="goodsCounter > 1">
              ({{ goodsCounter }})
            </span>
          </p>

          <FormInput v-model="autoData" type="textarea" :multiline-placeholder="$t('addProductAutoDataPlaceholder')" />

          <p :class="$style.info">
            {{ notLimited ? $t("autoDeliveryInfoNotLimited") : $t("autoDeliveryInfo") }}
          </p>
        </template>
        <template v-else-if="autoDataCheckbox">
          <p :class="$style.subtitle">
            {{ $t("autoDelivery") }}
          </p>

          <p :class="$style.info">
            {{ notLimited ? $t("autoDeliveryInfoNotLimited") : $t("autoDeliveryInfo") }}
          </p>

          <div v-for="(variant, index) in productVariants" :key="`auto_data_${variant.id}`" :class="$style.autoDataRow">
            <span>
              <span :class="$style.autoDataRow__number">{{ index + 1 }}.</span>
              {{ variant.title }} ({{ optionCountAmountWithAutoDelivery(variant) }})
            </span>

            <FormInput v-model="variantsAutoData[variant.id]" type="textarea" :multiline-placeholder="$t('addProductAutoDataPlaceholder')" />
          </div>
        </template>

        <Tabs :class="$style.tabs" :items="langs" :active-tab-index="activeLangIndex" @change="setActiveLang" />

        <p :class="$style.subtitle">
          {{ $t("shortDescription") }} ({{ activeLang?.title }})
        </p>

        <FormInput :key="`short__${activeLang?.value}`" v-model="shortDescription[activeLang?.value]" type="textarea" />

        <p :class="$style.subtitle">
          {{ $t("description") }} ({{ activeLang?.title }})
        </p>

        <FormInput :key="`full__${activeLang?.value}`" v-model="description[activeLang?.value]" type="textarea" />

        <div :class="$style.twoColumn">
          <div>
            <div>
              <p :class="$style.label">
                {{ $t('price') }}
              </p>

              <NumberInput
                v-if="!minVariantPrice"
                v-model="price"
                :class="$style.fullWidth"
                :increment="0.01"
                :fixed="2"
                real
                full-width
                currency="money"
              />

              <Currency v-else type="money" size="24" is-reversed real>
                <span :class="$style.subtitle">{{ minVariantPrice }}</span>
              </Currency>
            </div>
            <div v-if="showRub">
              <p :class="$style.label">
                Цена в рублях:
              </p>
              <Currency type="money" size="24" is-reversed rub>
                <span v-if="minVariantPrice" :class="$style.subtitle">{{ minVariantPriceInRub }}</span>
                <span v-else :class="$style.subtitle">{{ priceInRub }}</span>
              </Currency>
            </div>
          </div>
          <div v-if="!minVariantPrice">
            <div>
              <p :class="$style.label">
                {{ $t('amount') }}
              </p>

              <div v-if="autoDataCheckbox || notLimited" :class="$style.fieldBlocked">
                {{ goodsCounter }}
              </div>

              <NumberInput
                v-else
                v-model="amount"
                :class="$style.fullWidth"
                :increment="1"
                :max="9000000"
                :min="1"
                :fixed="0"
                full-width
              />
            </div>
            <div>
              <div :class="$style.checkbox">
                <CheckboxInput v-model="notLimited" />
                <span :class="$style.checkbox__label">
                  {{ $t("notLimited") }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <p :class="$style.subtitle">
          {{ $t("details") }}
        </p>

        <div :class="$style.details">
          <div :class="$style.detail">
            <span :class="$style.detail__key">
              {{ $t(activeServiceType) }}
            </span>
            <span :class="$style.detail__value">
              {{ currentAppName }}
            </span>
          </div>
          <div :class="$style.detail">
            <span :class="$style.detail__key">
              {{ $t('category') }}
            </span>
            <span :class="$style.detail__value">
              {{ categoryName }}
            </span>
          </div>
          <div :class="$style.detail">
            <span :class="$style.detail__key">
              {{ $t('amount') }}
            </span>
            <span :class="$style.detail__value">
              {{ minVariantPrice ? variantsStocksSum : (autoDataCheckbox || notLimited) ? goodsCounter : amount }}
            </span>
          </div>
          <div :class="$style.detail">
            <span :class="$style.detail__key">
              {{ $t('price') }}
            </span>

            <template v-if="minVariantPrice">
              <Currency v-if="+minVariantPrice > 0" type="money" real>
                <span :class="$style.detail__value">{{ minVariantPrice }}</span>
              </Currency>
              <span v-else :class="$style.detail__value">-</span>
            </template>
            <Currency v-else-if="price > 0" type="money" real>
              <span :class="$style.detail__value">{{ price }}</span>
            </Currency>
            <span v-else :class="$style.detail__value">-</span>
          </div>
          <div :class="$style.detail">
            <span :class="$style.detail__key">
              {{ $t('priceForCustomer') }}
            </span>
            <span :class="$style.detail__value">
              {{ customerPrice }}
            </span>
          </div>
        </div>

        <Button is-primary :class="$style.save" :disabled="!canCreate" @click="createManyProducts">
          {{ $t('buttonSave') }}
        </Button>
      </template>
    </div>
  </div>
</template>

<style lang="scss" module>
.page {
  width: 100%;

  &__body {
    padding: 16px;
  }
}

.subtitle {
  margin: 40px 0 24px;
  color: $white;
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
}

.label {
  margin: 24px 0 8px;
  color: $blue-grey10;
  font-size: 12px;
  font-weight: 500;
  line-height: 12px;
  text-transform: uppercase;

  &__comment {
    margin: 0 8px;
    color: $amber60;
    font-size: 10px;
    font-weight: 400;
    line-height: 12px;
    text-transform: uppercase;
  }
}

.fieldBlocked {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: 48px;
  width: 100%;
  padding: 0 16px;
  border-radius: 8px;
  background: $blue-grey70;
  color: $blue-grey10;
}

.selector {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 16px;

  &__variant {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    background: $blue-grey50;
    border: 2px solid $blue-grey50;
    border-radius: 12px;
    padding: 16px;
    color: $white;
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    cursor: pointer;

    &Active {
      border: 2px solid $primary60;
      cursor: auto;
    }
  }

  &__icon {
    width: 24px;
    height: 24px;
    padding: 2px;
    margin-right: 8px;

    &Round {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      border: 2px solid $blue-grey20;

      &Active {
        border: 2px solid $primary60;
      }
    }

    &Dot {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: $primary60;
    }
  }
}

.twoColumn {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 16px;
  align-items: start;
}

.fullWidth {
  width: 100%;
}

.details {
  display: grid;
  padding: 24px 0;
  grid-gap: 12px;
  grid-template-columns: 1fr;
  border-top: 1px solid $blue-grey40;
  border-bottom: 1px solid $blue-grey40;
}

.detail {
  display: flex;
  align-items: center;
  justify-content: space-between;

  &__key {
    color: $blue-grey10;
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;
  }

  &__value {
    color: $white;
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;
  }
}

.save {
  width: 100% !important;
  margin-top: 40px;

  &:disabled {
    opacity: 0.3;
  }
}

.addBtn {
  width: 100% !important;
  margin-top: 40px;

  &:disabled {
    opacity: 0.3;
  }
}

.closeBtn {
  width: 100% !important;
  margin-top: 8px;

  &:disabled {
    opacity: 0.3;
  }
}

.checkbox {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  margin: 32px 0 20px;
  color: $white;

  &__label {
    color: $white;
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;
  }
}

.info {
  margin: 8px 0;
  color: $blue-grey10;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.spaceBetween {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.block {
  background: $blue-grey70;
  border-radius: 8px;
  height: max-content;

  &__header {
    padding: 16px;
    border-bottom: 1px solid $blue-grey40;
  }

  &__body {
    padding: 16px;
  }

  &__title {
    color: $white;
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    line-height: 24px;
  }
}

.optionsList {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 32px;
  margin: 16px 0 24px;
}

.optionOption {
  padding: 16px;
  background: rgba($blue-grey30, 0.1);
  border-radius: 8px;

  &__title {
    display: grid;
    align-items: center;
    grid-template-columns: 1fr max-content;
    color: $blue-grey10;
    font-size: 12px;
    font-weight: 500;
    line-height: 12px;
    text-transform: uppercase;
  }
}

.option {
  width: 100%;

  &__title {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    color: $blue-grey10;
    font-size: 12px;
    font-weight: 500;
    line-height: 12px;
    text-transform: uppercase;
  }

  &__icon {
    width: 24px;
    height: 24px;
    margin-left: 16px;
    cursor: pointer;
  }

  &__comment {
    color: $white;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    margin: 8px 0;
  }

  &__textExample {
    margin: 8px 0;
    width: 100%;
    height: 44px;
    background: $blue-grey60;
    border-radius: 8px;
  }

  &__variantExample {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: $white;
    height: 40px;
    padding: 0 8px;
    background: $blue-grey60;
    border-radius: 8px;
    margin: 10px 0;

    &Left {
      display: flex;
      align-items: center;
      justify-content: flex-start;
    }

    &Name {
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
      text-overflow: ellipsis;
      margin: 0 8px;
    }

    &Amount {
      color: $blue-grey10;
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
      text-overflow: ellipsis;
    }

    &Price {
      font-size: 16px;
      font-weight: 500;
      line-height: 20px;
    }
  }
}

.noOptions {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  margin: 20px 0;

  &__icon {
    width: 32px;
    height: 32px;
    margin-right: 16px;
  }

  &__text {
    color: $white;
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;
  }
}

.optionsPanel {
  @media (min-width: $breakpointTablet) {
    display: grid;
    grid-template-columns: 300px 1fr;
    grid-gap: 24px;
  }

  @media (min-width: $breakpointDesktop) {
    display: grid;
    grid-template-columns: 384px 1fr;
    grid-gap: 24px;
  }
}

.modal {
  position: fixed;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: flex-end;
  width: 100%;
  height: 100%;
  z-index: 3000;
  background: rgba(#000, 0.5);

  @media (min-width: $breakpointTablet) {
    position: relative;
    background: transparent;
    align-items: flex-start;
    z-index: 1;
  }

  &__main {
    width: 100%;
    max-height: 100vh;
    overflow-y: auto;
    padding: 16px 16px 80px;
    background: $blue-grey70;
    color: $white;
    border-radius: 16px 16px 0 0;
  }

  &__title {
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px;
    padding: 16px 0;
    border-bottom: 1px solid $blue-grey40;

    @media (min-width: $breakpointTablet) {
      display: none;
    }
  }
}

.autoDataRow {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 8px;
  color: $white;
  margin-top: 12px;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;

  &__number {
    color: $blue-grey10;
    margin-right: 8px;
  }
}

.tabs {
  margin: 60px 0 0;
  height: 60px;
  background: $blue-grey60;
  padding: 12px 24px;
  border-radius: 8px;
  width: max-content;
}
</style>
